import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import { LegalTechnologyDeniedUseItemModel } from 'forms/sections/legal/technology/denied/types';
import {
  CommonScrollIntoView,
  CommonOnError,
  CommonRequired,
  CommonAddressBooks,
  CommonOnUpdate,
} from 'types/props';
import { Dispatch } from 'redux';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import * as formTypes from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import NotApplicable from 'components/Form/NotApplicable';
import Branch from 'components/Form/Branch';
import Dropdown from 'components/Form/Dropdown';
import PVQName from 'components/Form/PVQName';
import { systemITTypes, systemITLocationOptions, handingProtectedInformationFrequencyOptions } from 'constants/enums/informationTechnologyOptions';
import MultipleDropdown from 'components/Form/MultipleDropdown';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Actions from './Actions';

interface Props extends Partial<LegalTechnologyDeniedUseItemModel>, ValidationElementProps {
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  dispatch?: Dispatch
}

export default class DeniedItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalTechnologyDeniedUseItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Incident: this.props.Incident,
      Location: this.props.Location,
      Action: this.props.Action,
      DeniedLocations: this.props.DeniedLocations,
      SystemName: this.props.SystemName,
      SystemOwnerNotApplicable: this.props.SystemOwnerNotApplicable,
      SystemOwner: this.props.SystemOwner,
      InformationType: this.props.InformationType,
      InformationTypeOtherExplanation: this.props.InformationTypeOtherExplanation,
      HadSuccess: this.props.HadSuccess,
      IsShared: this.props.IsShared,
      SharedName: this.props.SharedName,
      Frequency: this.props.Frequency,
      FrequencyOtherExplanation: this.props.FrequencyOtherExplanation,
      Occurances: this.props.Occurances,
      OccurancesExplanation: this.props.OccurancesExplanation,
      UnauthorizedActions: this.props.UnauthorizedActions,
      ActionDetails: this.props.ActionDetails,
      ActionOtherDetails: this.props.ActionOtherDetails,
      Reason: this.props.Reason,
      ...queue,
    });
  };

  updateDate = (values: LegalTechnologyDeniedUseItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateIncident = (values: LegalTechnologyDeniedUseItemModel['Incident']) => {
    this.update({
      Incident: values,
    });
  };

  updateLocation = (values: LegalTechnologyDeniedUseItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateAction = (values: LegalTechnologyDeniedUseItemModel['Action']) => {
    this.update({
      Action: values,
    });
  };

  updateDeniedLocations = (values: LegalTechnologyDeniedUseItemModel['DeniedLocations']) => {
    const list = Checkbox.select(values, this.props.DeniedLocations);
    const includesOther = list.includes('Other');

    this.update({
      DeniedLocations: { values: list },
      Incident: includesOther ? this.props.Incident : {}
    });
  };

  updateUnauthorizedActions = (values: LegalTechnologyDeniedUseItemModel['UnauthorizedActions']) => {
    this.update({
      UnauthorizedActions: values,
    });
  };

  updateActionDetails = (values: LegalTechnologyDeniedUseItemModel['ActionDetails']) => {
    this.update({
      ActionDetails: values,
    });
  };

  updateActionOtherDetails = (values: LegalTechnologyDeniedUseItemModel['ActionOtherDetails']) => {
    this.update({
      ActionOtherDetails: values,
    });
  };

  updateSystemName = (values: LegalTechnologyDeniedUseItemModel['SystemName']) => {
    this.update({
      SystemName: values,
    });
  };

  updateSystemOwner = (values: LegalTechnologyDeniedUseItemModel['SystemOwner']) => {
    this.update({
      SystemOwner: values,
    });
  };

  updateSystemOwnerNotApplicable = (value: LegalTechnologyDeniedUseItemModel['SystemOwnerNotApplicable']) => {
    this.update({
      SystemOwner: value.applicable ? this.props.SystemOwner : {},
      SystemOwnerNotApplicable: value,
    });
  };

  updateHasUnauthorizedSuccess = (value: LegalTechnologyDeniedUseItemModel['HasUnauthorizedSuccess']) => {
    this.update({
      HasUnauthorizedSuccess: value,
    });
  };

  updateInformationType = (values: LegalTechnologyDeniedUseItemModel['InformationType']) => {
    this.update({
      InformationType: values,
    });
  };

  updateInformationTypeOtherExplanation = (values: LegalTechnologyDeniedUseItemModel['InformationTypeOtherExplanation']) => {
    this.update({
      InformationTypeOtherExplanation: values,
    });
  };

  updateOccurances = (values: LegalTechnologyDeniedUseItemModel['Occurances']) => {
    this.update({
      Occurances: values,
    });
  };

  updateSharedName = (values: LegalTechnologyDeniedUseItemModel['SharedName']) => {
    this.update({
      SharedName: values,
    });
  };

  updateOccurancesExplanation = (values: LegalTechnologyDeniedUseItemModel['OccurancesExplanation']) => {
    this.update({
      OccurancesExplanation: values,
    });
  };

  updateFrequency = (values: LegalTechnologyDeniedUseItemModel['Frequency']) => {
    this.update({
      Frequency: values,
    });
  };

  updateReason = (values: LegalTechnologyDeniedUseItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateFrequencyOtherExplanation = (values: LegalTechnologyDeniedUseItemModel['FrequencyOtherExplanation']) => {
    this.update({
      FrequencyOtherExplanation: values,
    });
  };

  updateHadSuccess = (values: LegalTechnologyDeniedUseItemModel['HadSuccess']) => {
    const additionalUpdates = values?.value && values.value !== 'Yes' ? {
      InformationType: { value: '' },
      InformationTypeOtherExplanation: {},
    } : {};
    this.update({
      HadSuccess: values,
      ...additionalUpdates,
    });
  };

  updateIsShared = (values: LegalTechnologyDeniedUseItemModel['IsShared']) => {
    this.update({
      IsShared: values,

    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const actionother = Array.isArray(this.props.UnauthorizedActions?.values) && this.props.UnauthorizedActions.values.includes('Other');
    const pvqPrefix = `legal.pvq.technology.denied`;
    return (
      <div>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.date`)}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="Date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.incidentplace`)}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DeniedLocations`}
        >

          <CheckboxGroup
            className="denied-access-location option-list option-list-vertical"
            attribute={`${attribute}.DeniedLocations`}
            onError={this.handleError}
            selectedValues={this.props.DeniedLocations?.values}
            required={this.props.required}
          >
            {systemITLocationOptions.map((s) => (
              <Checkbox
                key={s}
                name={s}
                label={s}
                value={s}
                onUpdate={this.updateDeniedLocations}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>

          {
            this.props.DeniedLocations?.values?.includes('Other') && (
            <Textarea
              name="Incident"
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              {...this.props.Incident}
              onUpdate={this.updateIncident}
              onError={this.props.onError}
              className="Incident"
              required={this.props.required}
              attribute={`${attribute}.Incident`}
            />

            )
       }
        </Field>
        <Field
          attribute={`${attribute}.Location`}
          optional
          onError={this.props.onError}
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocation
            name="pvqlocaton"
            {...this.props.Location}
            label={i18n.t(`${pvqPrefix}.heading.inUS`)}
            title={i18n.t(`${pvqPrefix}.heading.inUS`)}
            domesticTitlel={i18n.t(`${pvqPrefix}.heading.usaddress`)}
            foreignTitlel={i18n.t(`${pvqPrefix}.heading.nonusaddress`)}
            onUpdate={this.updateLocation}
            onError={this.props.onError}
            className="pvqlocation"
            layout={PVQLocation.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION}
            geocode
            showPostOffice
            addressBooks={this.props.addressBooks}
            addressBook="Incident"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Location`}
            showMilitaryInstallation
          />
        </Field>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.systemname`)}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SystemName`}
        >
          <Text
            name="system-name"
            {...this.props.SystemName}
            className="system-name"
            maxlength="100"
            onUpdate={this.updateSystemName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.SystemName`}
          />
        </Field>

        <Field
          title={i18n.t(`${pvqPrefix}.heading.systemowner`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SystemOwner`}
        >
          <NotApplicable
            name="system-owner"
            label={i18n.t(`${pvqPrefix}.heading.idk`)}
            or={i18n.t(`${pvqPrefix}.heading.or`)}
            {...this.props.SystemOwnerNotApplicable}
            onUpdate={this.updateSystemOwnerNotApplicable}
            className="system-owner"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.SystemOwnerNotApplicable`}
            includeNAConfirmation={Boolean(this.props.SystemOwner?.value)}
          >
            <Text
              name="legal-pvq-technology-unauthorized-sysowner"
              className="legal-pvq-technology-unauthorized-sysowner-na"
              {...this.props.SystemOwner}
              onError={this.props.onError}
              onUpdate={this.updateSystemOwner}
              required={this.props.required}
              attribute={`${attribute}.SystemOwner`}
            />
          </NotApplicable>
        </Field>

        <Branch
          attribute={`${attribute}.HadSuccess`}
          name="system-success"
          label={i18n.t(`${pvqPrefix}.heading.systemsuccess`)}
          labelSize="h4"
          {...this.props.HadSuccess}
          onUpdate={this.updateHadSuccess}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.HadSuccess?.value === 'Yes' && (
        <>
          <Field
            title={i18n.t(`${pvqPrefix}.heading.infotype`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >

            <Dropdown
              attribute={`${attribute}.InformationType`}
              name="information-type"
              title={i18n.t(`${pvqPrefix}.heading.infotype`)}
              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateInformationType}
              value={this.props.InformationType.value}
              required={this.props.required}
            >
              {systemITTypes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>

            {this.props.InformationType?.value === 'Other' && (

            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="system-other"
              attribute={`${attribute}.InformationTypeOtherExplanation`}
              {...this.props.InformationTypeOtherExplanation}
              onUpdate={this.updateInformationTypeOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>
          <Field
            title={i18n.t(`${pvqPrefix}.heading.systemtimes`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >
            <Dropdown
              attribute={`${attribute}.Frequency`}
              name="frequency"

              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateFrequency}
              value={this.props.Frequency.value}
              required={this.props.required}
            >
              {handingProtectedInformationFrequencyOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
            {this.props.Frequency?.value === 'Other' && (

            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="frequency-explain"
              attribute={`${attribute}.FrequencyOtherExplanation`}
              {...this.props.FrequencyOtherExplanation}
              onUpdate={this.updateFrequencyOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>
          {/* <Branch
               attribute={`${attribute}.IsShared`}
               name="sys-isshared"
               label={i18n.t(`${pvqPrefix}.heading.systemshare`)}
               labelSize="h4"
               {...this.props.IsShared}
               onUpdate={this.updateIsShared}
               onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
               required={this.props.required}
               scrollIntoView={this.props.scrollIntoView}
             />
             {this.props.IsShared?.value === 'Yes' && (
             <Field
               attribute={`${attribute}.SharedName`}
               title={i18n.t(`${pvqPrefix}.heading.systemwho`)}
               titleSize="h4"
               className="shared-name"
               scrollIntoView={this.props.scrollIntoView}
             >
               <PVQName
                 attribute={`${attribute}.SharedName`}
                 exactAttribute
                 aria-label="Provide full name"
                 name="shared-name"
                 {...this.props.SharedName}
                 onUpdate={this.updateSharedName}
                 onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
                 required={this.props.required}
                 scrollIntoView={this.props.scrollIntoView}
                 hideNoFirstName
               />
             </Field>
             )} */}
          <Field
            title={i18n.t(`${pvqPrefix}.heading.systemoccurances`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >
            <Dropdown
              attribute={`${attribute}.Occurances`}
              name="sys-occurancnces"

              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateOccurances}
              value={this.props.Occurances.value}
              required={this.props.required}
            >
              {handingProtectedInformationFrequencyOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
            {this.props.Occurances?.value === 'Other' && (
            <Textarea

              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="sys-occurancnces_expl"
              attribute={`${attribute}.OccurancesExplanation`}
              {...this.props.OccurancesExplanation}
              onUpdate={this.updateOccurancesExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>

          <Field
            attribute={`${attribute}.Reason`}
            scrollIntoView={this.props.scrollIntoView}
            title={i18n.t(`${pvqPrefix}.heading.systemwhy`)}
          >
            <Textarea
              title={i18n.t(`${pvqPrefix}.heading.systemwhy`)}
              name="reason"
              attribute={`${attribute}.Reason`}
              {...this.props.Reason}
              onUpdate={this.updateReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.UnauthorizedActions`}
            title={i18n.t(`${pvqPrefix}.heading.systemget`)}
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
          >

            <Actions
              attribute={`${attribute}.UnauthorizedActions`}
              name="actions"
              className="incident-UnauthorizedActions"
              {...this.props.UnauthorizedActions}
              onUpdate={this.updateUnauthorizedActions}
              onError={this.props.onError}
              required={this.props.required}
            />
            <Textarea
              label={i18n.t(`${pvqPrefix}.heading.actiondetails`)}
              name="actions-details"
              attribute={`${attribute}.ActionDetails`}
              {...this.props.ActionDetails}
              onUpdate={this.updateActionDetails}
              onError={this.props.onError}
              required={this.props.required}
            />
            {actionother && (
            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="details-other"
              attribute={`${attribute}.ActionDetailsOther`}
              {...this.props.ActionOtherDetails}
              onUpdate={this.updateActionOtherDetails}
              onError={this.props.onError}
              required={this.props.required}
            />
            )}
          </Field>
        </>

        )}

      </div>
    );
  }
}

DeniedItem.defaultProps = {
  onUpdate: (queue) => {},
  onError: (value, arr) => arr,
  DeniedLocations: {},
  SystemName: {},
  SystemOwner: {},
  SystemOwnerNotApplicable: { applicable: true },
  HasUnauthorizedSuccess: {},
  HadSucess: {},
  InformationType: {},
  InformationTypeOtherExplanation: {},
  Frequency: {},
  FrequencyOtherExplanation: {},
  IsShared: {},
  SharedName: {},
  Occurances: {},
  OccurancesExplanation: {},
  UnauthorizedActions: {},
  ActionDetails: {},
  ActionDetailsOther: {},
  Reason: {},
};
